import { isNumeric } from '../../util/helpers';

import { LatLon } from '../../util/geodesy/latlon';
import { parseLatLon } from '../util/parseUtils';
import { ApproachLighting } from './approach-lighting.model';
import { Obstacle } from './obstacle.model';


/**
 * Instrument Landing System (ILS) type
 */
export enum ILSType {
  /**
   * Instrument Landing System
   */
  ILS,

  /**
   * Microwave Landing System
   */
  MLS,

  /**
   * Simplified Directional Facility
   */
  SDF,

  /**
   * Localizer
   */
  LOC,

  /**
   * Localizer-type Directional Aid
   */
  LDA,

  /**
   * Interim Standard Microwave Landing System
   */
  ISMLS,

  /**
   * Instrument Landing System / Distance Measuring Equipment
   */
  ILS_DME,

  /**
   * Simplified Directional Facility / Distance Measuring Equipment
   */
  SDF_DME,

  /**
   * Localizer / Distance Measuring Equipment
   */
  LOC_DME,

  /**
   * Localizer / Glide Slope
   */
  LOC_GS,

  /**
   * Localizer-Type Directional Aid / Distance Measuring Equipment
   */
  LDA_DME
}


/**
 * Condition of runway markings
 */
export enum RunwayMarkingsCondition {
  Good,
  Fair,
  Poor
}


/**
 * Traffic pattern direction
 */
export enum TrafficPatternDirection {
  Left,
  Right
}


/**
 * Takeoff/Land area (TOLA) model. For runways, there is a separate takeoff/land area per reciprocal end of the runway
 * (the model for the runway as a whole is LandingSurface).
 */
export class TakeoffLandArea {
  /**
   * Database Id for the takeoff/land area
   */
  takeoffLandAreaId: number;

  /**
   * True if this is the base end of the runway as opposed to the reciprocal end. This will typically be the lower
   * number (e.g., runway "05" will be the base, as opposed to "23"). For helipads, there is no concept of base or
   * reciprocal end, so this field will be null.
   */
  base: boolean;

  /**
   * Display name of the takeoff/land area (e.g., "05")
   */
  name: string;

  /**
   * Elevation (in feet MSL) at physical runway end
   */
  elevation: number;

  /**
   * Elevation (in feet MSL) at displaced threshold, or null if there is no displaced threshold.
   */
  dispThresholdElev: number;

  /**
   * Elevation (in feet MSL) at touchdown zone, or null if no data available.
   */
  touchDownZoneElev: number;

  /**
   * Length of displaced threshold in feet from runway end, or null if there is no displaced threshold.
   */
  dispThresholdDistance: number;

  /**
   * True heading of the runway to the nearest degree (if available)
   */
  trueAlignment: number;

  /**
   * Type of Instrument Landing System (ILS), if any
   */
  ilsType: ILSType;

  /**
   * Type of runway markings present (if reported)
   */
  markingsType: string;

  /**
   * Condition of runway markings (if reported)
   */
  markingsCondition: RunwayMarkingsCondition;

  /**
   * Traffic pattern direction for fixed-wing aircraft
   */
  trafficPatDirFixed: TrafficPatternDirection;

  /**
   * Visual glide path angle in hundredths of degrees (if reported)
   */
  glidePathAngle: number|any;

  /**
   * Height that the effective visual glide path crosses above the runway threshold in feet AGL (if reported)
   */
  thresholdXingHeight: number|any;

  /**
   * Runway gradient (i.e., slope) in degrees
   */
  gradient: number|any;

  /**
   * Takeoff run distance available (TORA), in feet
   */
  takeoffRunAvailable: number|any;

  /**
   * Landing distance available (LDA), in feet
   */
  landingDistanceAvail: number|any;

  /**
   * Accelerate/Stop distance available (ASDA)
   */
  stopDistanceAvailable: number|any;

  /**
   * Takeoff/land area latitude and longitude
   */
  location?: LatLon;

  /**
   * Types and descriptions of approach lighting systems present at this takeoff/land area
   */
  approachLighting: ApproachLighting[];

  /**
   * Obstacles affecting operation at this takeoff/landing area
   */
  obstacles: Obstacle[];

  /**
   * Parses takeoff/land area
   * @param data {any} Takeoff/land area as returned by API
   * @return {TakeoffLandArea} Parsed takeoff/land area
   */
  static parse(data: any): TakeoffLandArea {
    return {
      takeoffLandAreaId: data.takeoffLandAreaId,
      base: this.parseBase(data.baseOrRecip),
      name: data.tlaName,
      elevation: isNumeric(data.elev) ? +data.elev : null,
      dispThresholdElev: isNumeric(data.dispThresholdEleve) ? +data.dispThresholdEleve : null,
      touchDownZoneElev: isNumeric(data.touchDownZoneElev) ? +data.touchDownZoneElev : null,
      dispThresholdDistance: isNumeric(data.dispThresholdDistance) ? +data.dispThresholdDistance : null,
      trueAlignment: isNumeric(data.trueAlignment) ? +data.trueAlignment : null,
      ilsType: this.parseILSType(data.ilsType),
      markingsType: data.markingsType,
      markingsCondition: this.parseRunwayMarkingsCondition(data.markingsCondition),
      trafficPatDirFixed: this.parseTrafficPatternDirection(data.trafficPatDirFixed),
      glidePathAngle: isNumeric(data.glidePathAngle) ? +data.glidePathAngle : null,
      thresholdXingHeight: isNumeric(data.thresholdXingHeight) ? +data.thresholdXingHeight : null,
      gradient: isNumeric(data.gradient) ? +data.gradient : null,
      takeoffRunAvailable: isNumeric(data.takeoffRunAvailable) ? +data.takeoffRunAvailable : null,
      landingDistanceAvail: isNumeric(data.landingDistanceAvail) ? +data.landingDistanceAvail : null,
      stopDistanceAvailable: isNumeric(data.stopDistanceAvailable) ? +data.stopDistanceAvailable : null,
      location: parseLatLon(data.latitude, data.longitude),
      approachLighting: (data.lightings || []).map(d => ApproachLighting.parse(d)),
      obstacles: (data.obstacles || []).map(d => Obstacle.parse(d))
    };
  }

  /**
   * Parses the baseOrRecip field from the API as a boolean
   * @param baseOrRecip {string} baseOrRecip as returned by API
   * @return {boolean} True if this takeoff/land area represents the "base" end of the runway, false if it is the
   *    "reciprocal" end, or null if the base/reciprocal concept does not apply (e.g., for helipads).
   */
  static parseBase(baseOrRecip: string): boolean {
    if (baseOrRecip != null) {
      baseOrRecip = baseOrRecip.toLowerCase();
    }
    switch (baseOrRecip) {
      case 'base':
        return true;
      case 'reciprocal':
        return false;
      default:
        return null;
    }
  }

  /**
   * Parses ILS type from string
   * @param value {string} ILS type as returned by API
   * @return {ILSType} Parsed ILS Type
   */
  static parseILSType(value: string): ILSType {
    switch (value) {
      case 'ILS':
        return ILSType.ILS;
      case 'MLS':
        return ILSType.MLS;
      case 'SDF':
        return ILSType.SDF;
      case 'LOCALIZER':
        return ILSType.LOC;
      case 'LDA':
        return ILSType.LDA;
      case 'ISMLS':
        return ILSType.ISMLS;
      case 'ILS/DME':
        return ILSType.ILS_DME;
      case 'SDF/DME':
        return ILSType.SDF_DME;
      case 'LOC/DME':
        return ILSType.LOC_DME;
      case 'LOC/GS':
        return ILSType.LOC_GS;
      case 'LDA/DME':
        return ILSType.LDA_DME;
      default:
        return null;
    }
  }

  /**
   * Formats ILS type for display
   */
  static formatILSType(value: ILSType): string {
    switch (value) {
      case ILSType.ILS:
        return 'ILS';
      case ILSType.MLS:
        return 'MLS';
      case ILSType.SDF:
        return 'SDF';
      case ILSType.LOC:
        return 'Localizer';
      case ILSType.LDA:
        return 'LDA';
      case ILSType.ISMLS:
        return 'ISMLS';
      case ILSType.ILS_DME:
        return 'ILS/DME';
      case ILSType.SDF_DME:
        return 'SDF/DME';
      case ILSType.LOC_DME:
        return 'LOC/DME';
      case ILSType.LOC_GS:
        return 'LOC/GS';
      case ILSType.LDA_DME:
        return 'LDA/DME';
      default:
        return null;
    }
  }

  /**
   * Parses runway markings condition from string
   * @param value {string} Runway markings condition as returned by API
   * @return {RunwayMarkingsCondition} Parsed runway markings condition
   */
  static parseRunwayMarkingsCondition(value: string): RunwayMarkingsCondition {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'good':
        return RunwayMarkingsCondition.Good;
      case 'fair':
        return RunwayMarkingsCondition.Fair;
      case 'poor':
        return RunwayMarkingsCondition.Poor;
      default:
        return null;
    }
  }

  /**
   * Formats runway markings condition for display
   */
  static formatRunwayMarkingsCondition(value: RunwayMarkingsCondition): string {
    switch (value) {
      case RunwayMarkingsCondition.Good:
        return 'Good';
      case RunwayMarkingsCondition.Fair:
        return 'Fair';
      case RunwayMarkingsCondition.Poor:
        return 'Poor';
      default:
        return null;
    }
  }

  /**
   * Parses traffic pattern direction from string
   * @param value {string} Traffic pattern direction as returned by API
   * @return {TrafficPatternDirection} Parsed traffic pattern direction
   */
  static parseTrafficPatternDirection(value: string): TrafficPatternDirection {
    if (value != null) {
      value = value.toLowerCase();
    }
    switch (value) {
      case 'left':
        return TrafficPatternDirection.Left;
      case 'right':
        return TrafficPatternDirection.Right;
      default:
        return null;
    }
  }

  /**
   * Formats traffic pattern direction for display
   */
  static formatTrafficPatternDirection(value: TrafficPatternDirection): string {
    switch (value) {
      case TrafficPatternDirection.Left:
        return 'Left';
      case TrafficPatternDirection.Right:
        return 'Right';
      default:
        return '';
    }
  }
}
